import React, { useMemo } from "react";
import CallInitiatedIcon from "../../assets/svg/call-initiated-icon";
import CallNotAnsweredIcon from "../../assets/svg/call-not-answered-icon";
import LineArrowRightIcon from "../../assets/svg/line-arrow-right-icon";
import VerbamessengerIcon from "../../assets/svg/verbamessenger-icon";
import VerbamailIcon from "../../assets/svg/verbamail-icon";
import VerbacallIcon from "../../assets/svg/verbacall-icon";
import VerbachatIcon from "../../assets/svg/verbachat-icon";
import ClockIcon from "../../assets/svg/clock-icon";
import VerbafloLogoIcon from "../../assets/images/verbaflo-logo.png";
import UserIcon from "../../assets/svg/user-icon";
import { Avatar } from "../Avatar/Avatar";
import { getDateTimeByFormat } from "../../redux/slices/dateTimeFormatSlice";
import { Tooltip } from "../Tooltip/Tooltip";
import { useSearchHighlight } from "../../context/SearchHighlightContext";
import { highlightTextInPlain } from "../../pages/Communications/utils/highlightSearchText";

type Modality = "email" | "call" | "messenger" | "chat";
type CallStatus = "initiated" | "not-answered";

interface VoiceCallMessageBlockProps {
  callStatus: CallStatus;
  callText: string;
  modality?: Modality;
  isSent?: boolean;
  showAiResponseTime?: boolean;
  showTimestamp?: boolean;
  iconType?: "verbaflo" | "participant" | "agent";
  participantInfo?: {
    text: string;
    size?: number;
    showOnlineStatus?: boolean;
    isOnline?: boolean;
  };
  isOutbound?: boolean;
  messageContainerClassName?: string;
  messageId?: string;
  timestamp?: string;
  campaignName?: string;
}

const VoiceCallMessageBlock: React.FC<VoiceCallMessageBlockProps> = ({
  callStatus,
  callText,
  modality = "call",
  isSent = false,
  showAiResponseTime = false,
  showTimestamp = true,
  iconType,
  participantInfo,
  isOutbound = false,
  messageContainerClassName,
  messageId,
  timestamp,
  campaignName,
}) => {
  const modalityConfig = {
    email: {
      icon: VerbamailIcon,
      label: "VerbaMail",
      color: "text-verba-mail dark:text-verba-mail-dk",
    },
    call: {
      icon: VerbacallIcon,
      label: "VerbaCall",
      color: "text-verba-lavender dark:text-verba-lavender-dk",
    },
    messenger: {
      icon: VerbamessengerIcon,
      label: "VerbaMessenger",
      color: "text-verba-messenger dark:text-verba-messenger-dk",
    },
    chat: {
      icon: VerbachatIcon,
      label: "VerbaChat",
      color: "text-verba-chat dark:text-verba-chat-dk",
    },
  };

  const callStatusConfig = {
    initiated: {
      icon: CallInitiatedIcon,
      color: "text-verba-green-success dark:text-verba-green-success-dk",
    },
    "not-answered": {
      icon: CallNotAnsweredIcon,
      color: "text-verba-red-alert dark:text-verba-red-alert-dk",
    },
  };

  const { icon: ModalityIcon, label, color } = modalityConfig[modality];
  const { icon: CallStatusIcon, color: callStatusColor } =
    callStatusConfig[callStatus];

  const { searchQuery } = useSearchHighlight();

  const highlightedCallText = useMemo(() => {
    if (!callText) return "";
    
    if (searchQuery && searchQuery.trim() !== "") {
      return highlightTextInPlain(callText, searchQuery);
    }
    
    return callText;
  }, [callText, searchQuery]);

  const renderIcon = () => {
    if (!iconType) return null;

    return (
      <>
        {iconType === "verbaflo" && (
          <div>
            <img
              src={VerbafloLogoIcon}
              alt="Verbaflo Logo"
              className="w-[24px] h-[24px]"
            />
          </div>
        )}
        {iconType === "participant" && participantInfo && (
          <div className="shrink-0 relative">
            <Avatar
              size={participantInfo.size || 24}
              text={participantInfo.text}
              className="shrink-0"
            />
            {participantInfo.showOnlineStatus && (
              <div className="w-[6px] h-[6px] bg-pure-white dark:bg-pure-white-dk rounded-full absolute bottom-0 right-0 flex flex-col items-center justify-center">
                <div
                  className={`w-[4px] h-[4px] ${
                    participantInfo.isOnline
                      ? "bg-verba-green-success dark:bg-verba-green-success-dk"
                      : "bg-offline dark:bg-offline-dk"
                  } rounded-full`}
                ></div>
              </div>
            )}
          </div>
        )}
        {iconType === "agent" && (
          <div>
            <UserIcon className="w-[9px] h-[13px] text-verba-indigo dark:text-verba-indigo-dk" />
          </div>
        )}
      </>
    );
  };

  return (
    <div
      className={`w-fit flex flex-col gap-2 mb-4 ${
        isSent ? "ml-auto" : "mr-auto"
      }`}
      data-message-id={messageId}
    >
      <div
        className={`flex w-fit flex-col gap-3 px-3 py-3 rounded-[10px] ${
          isSent ? "ml-auto " : "mr-auto "
        } ${messageContainerClassName} message-card`}
      >
        <div className="flex items-center gap-[4px]">
          <CallStatusIcon className={`w-[18px] h-[18px] ${callStatusColor}`} />
          <p
            className="text-md font-medium text-heading-text dark:text-heading-text-dk"
            dangerouslySetInnerHTML={{ __html: highlightedCallText }}
          />
        </div>
      </div>

      <div
        className={`flex items-center justify-end gap-[4px] mt-[-5px] ${
          isSent ? "flex-row" : "flex-row-reverse"
        }`}
      >
        {isOutbound && (
          <Tooltip content={campaignName || ""}>
            <div className="flex items-center cursor-pointer">
              <LineArrowRightIcon className="w-[14px] h-[14px] text-verba-indigo dark:text-verba-indigo-dk" />
            </div>
          </Tooltip>
        )}
        <div className="flex items-center gap-[2px]">
          <ModalityIcon className={`w-[14px] h-[14px] ${color}`} />
          <p className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
            {label}
          </p>
        </div>

        {showAiResponseTime && (
          <>
            <div className="w-[2px] h-[2px] bg-gray-placeholder dark:bg-gray-placeholder-dk rounded-full"></div>
            <div className="flex items-center gap-[2px]">
              <ClockIcon className="w-[11px] h-[11px] text-gray-placeholder dark:text-gray-placeholder-dk" />
              <p className="text-sm font-medium text-gray-placeholder dark:text-gray-placeholder-dk leading-none">
                5.4s
              </p>
            </div>
          </>
        )}

        {showTimestamp && (
          <>
            <div className="w-[2px] h-[2px] bg-gray-placeholder dark:bg-gray-placeholder-dk rounded-full"></div>
            <div>
              <p className="text-sm font-medium text-gray-placeholder dark:text-gray-placeholder-dk">
                {getDateTimeByFormat(new Date(timestamp || "")).time}
              </p>
            </div>
          </>
        )}

        {iconType && (
          <>
            <div className="w-[2px] h-[2px] bg-gray-placeholder dark:bg-gray-placeholder-dk rounded-full"></div>
            {renderIcon()}
          </>
        )}
      </div>
    </div>
  );
};

export default VoiceCallMessageBlock;
